import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { DateRange } from 'types/date-range';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import { useWeekStartDay } from 'modules/common/hooks';
import { getRangeHonoringWeekStartDay } from 'utils/date-range';
import { TimelinePeriodMenu, TIMELINE_PERIOD } from './Timeline';
import {
  useNotesFilterContext,
  NOTES_PAGE_TYPE
} from './NotesFilterContextProvider';
import { TimesheetPeriodType } from './timesheetPeriod/TimesheetPeriodSelector';
import { DateRangeSelector } from './DateRangeSelector';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: ({ isTimelineView }: { isTimelineView: boolean }) =>
      isTimelineView ? 0 : theme.spacing(1),
    marginRight: ({ isTimelineView }: { isTimelineView: boolean }) =>
      isTimelineView ? 0 : theme.spacing(3),
    minWidth: theme.spacing(56)
  },
  dateFilter: {
    margin: ({ isTimelineView }: { isTimelineView: boolean }) =>
      isTimelineView ? 0 : theme.spacing(0, 2),
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: theme.spacing(5)
  }
}));

type NotesDateRangeSelectorProps = {
  children?: React.ReactNode;
};

const NotesDateRangeSelectorFC: React.FC<NotesDateRangeSelectorProps> = ({
  children
}: NotesDateRangeSelectorProps) => {
  const weekStart = useWeekStartDay();
  const {
    filterContext: {
      selectedDateRange: { start, end },
      pageType,
      timelinePeriod
    },
    setFilterContext
  } = useNotesFilterContext();

  const isTimelineView = useMemo(() => pageType === NOTES_PAGE_TYPE.TIMELINE, [
    pageType
  ]);
  const classes = useStyles({ isTimelineView });

  const { clearForm } = useNotesSelectionReset();

  const onDateRangeChange = useCallback(
    (range?: DateRange) => {
      if (range) {
        setFilterContext((previous) => ({
          ...previous,
          ...{
            selectedDateRange: range,
            selectedPeriod: TimesheetPeriodType.Custom
          }
        }));
        clearForm();
      }
    },
    [clearForm, setFilterContext]
  );

  const dateRange = useMemo(() => start && end && { start, end }, [start, end]);

  const onPeriodChange = useCallback(
    (event) => {
      const period = event.target.value as TIMELINE_PERIOD;
      switch (period) {
        case TIMELINE_PERIOD.WEEK: {
          const { start, end } = getRangeHonoringWeekStartDay({ weekStart });
          setFilterContext((previous) => ({
            ...previous,
            selectedDateRange: {
              start: start,
              end: end
            },
            timelinePeriod: period
          }));
          break;
        }

        case TIMELINE_PERIOD.DAY:
          setFilterContext((previous) => ({
            ...previous,
            selectedDateRange: {
              start: DateTime.local(),
              end: DateTime.local()
            },
            timelinePeriod: period
          }));
          break;
      }
      clearForm();
    },
    [clearForm, setFilterContext, weekStart]
  );

  if (!dateRange) {
    return <></>;
  }

  return (
    <Box className={classes.root} data-testid="notes-date-selector">
      <Box className={classes.dateFilter}>
        <DateRangeSelector
          selectedDateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          showSingleDay={isTimelineView}
          readOnly={timelinePeriod === TIMELINE_PERIOD.WEEK}
        />
        {children}
        {isTimelineView && (
          <TimelinePeriodMenu
            period={timelinePeriod}
            onPeriodChange={onPeriodChange}
          />
        )}
      </Box>
    </Box>
  );
};

export const NotesDateRangeSelector = React.memo(NotesDateRangeSelectorFC);
